import { useEffect, useMemo, useState } from "react";
import { AlertCircle, TrendingUp } from "lucide-react";
import { Label } from "@/components/ui/label";
import { groupDigits, parseNumber } from "@/lib/format";
import { HelperTooltip } from "@/components/calculator/fields/HelperTooltip";
import { useFieldRef } from "@/components/calculator/fields/useFieldRef";

/**
 * The note under a money field.
 *
 * An "emphasis" field is one where the figure drives the whole price, so its
 * help gets the tinted callout rather than the quiet grey line.
 */
const FieldHelp = ({ field, inputId }) => {
  if (field.emphasis) {
    return (
      <p
        id={`${inputId}-help`}
        data-testid={`currency-emphasis-${field.path}`}
        className="mt-2.5 flex items-start gap-2 rounded-lg border border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-3 py-2.5 text-[13px] leading-relaxed text-[color:var(--tt-teal-deep)]"
      >
        <TrendingUp className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{field.help}</span>
      </p>
    );
  }
  if (field.help) {
    return (
      <p id={`${inputId}-help`} className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
        {field.help}
      </p>
    );
  }
  return null;
};

/**
 * Rand input. Groups digits as the customer types, keeps the raw number in
 * state, and says plainly when a figure is what actually drives the price.
 */
export const CurrencyField = ({
  field,
  value,
  onChange,
  error,
  autoFocus = false,
  registerRef,
}) => {
  const inputRef = useFieldRef({ path: field.path, registerRef, autoFocus });
  const [text, setText] = useState(value === null || value === undefined ? "" : groupDigits(value));
  const [touched, setTouched] = useState(false);

  // Only re-format when the stored figure and the text on screen disagree, so
  // grouping a saved answer never disturbs somebody who is still typing.
  useEffect(() => {
    const incoming = value === null || value === undefined || value === "" ? "" : String(value);
    if (parseNumber(text) !== parseNumber(incoming)) {
      setText(incoming === "" ? "" : groupDigits(incoming));
    }
  }, [value, text]);

  const allowNegative = field.allowNegative === true;
  const inputId = `field-${field.path.replace(/\./g, "-")}`;
  const describedBy = useMemo(
    () => [field.help ? `${inputId}-help` : null, error ? `${inputId}-error` : null]
      .filter(Boolean)
      .join(" ") || undefined,
    [field.help, error, inputId],
  );

  const handleChange = (event) => {
    const raw = event.target.value;
    let cleaned = raw.replace(/[^0-9.,\-]/g, "");
    if (!allowNegative) cleaned = cleaned.replace(/-/g, "");
    else cleaned = cleaned.replace(/(?!^)-/g, "");
    const formatted = groupDigits(cleaned);
    setText(formatted);
    const parsed = parseNumber(formatted);
    onChange(parsed === null ? "" : parsed);
  };

  return (
    <div data-testid={`currency-field-${field.path}`}>
      <div className="flex items-start gap-2">
        <Label htmlFor={inputId} className="text-[15px] font-semibold leading-snug text-foreground">
          {field.label}
        </Label>
        <HelperTooltip text={field.tooltip} />
      </div>
      {field.sublabel ? (
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{field.sublabel}</p>
      ) : null}

      <div className="relative mt-3">
        <span
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base font-semibold text-muted-foreground"
          aria-hidden="true"
        >
          R
        </span>
        <input
          id={inputId}
          ref={inputRef}
          data-testid={`currency-input-${field.path}`}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={text}
          onChange={handleChange}
          onBlur={() => setTouched(true)}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={describedBy}
          placeholder="0"
          className={`tt-no-spin h-14 w-full rounded-lg border bg-white pl-10 pr-4 text-right font-amount text-xl font-semibold text-foreground outline-none transition-[border-color,box-shadow] duration-150 placeholder:font-normal placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-primary/25 ${
            error ? "border-destructive" : "border-input"
          }`}
        />
      </div>

      <FieldHelp field={field} inputId={inputId} />

      {error ? (
        <p
          id={`${inputId}-error`}
          data-testid={`field-error-${field.path}`}
          role="alert"
          className="mt-2 flex items-start gap-1.5 text-[13px] font-medium text-destructive"
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : (
        touched && null
      )}
    </div>
  );
};
